import React from "react";
import type {
  SMSPlaceholderDefinition,
  SMSTemplate,
} from "../../../../api/smsTemplates/SMSTemplate.typed";
import { useSMSTemplate } from "../../../../contexts/api/smsTemplates";
import { useAuth } from "../../../../contexts/api";
import { useAlert } from "../../../../contexts/app/notificationToast";
import { useConfirmationModal } from "../../../../contexts/app/confirmation";
import { usePermissions } from "../../../../contexts/app/permissions";
import { renderSMSPreviewBody } from "../smsEditorShared";
import type { SMSMessageChipEditorHandle } from "../../../shared/sms/SMSMessageChipEditor";

function emptyDraft(): SMSTemplate {
  return { name: "", body: "", active: true };
}

export function useSMSTemplatesPageState() {
  const {
    listSMSPlaceholders,
    listSMSTemplates,
    createSMSTemplate,
    updateSMSTemplate,
    deleteSMSTemplate,
  } = useSMSTemplate();
  const { user, userCompanies } = useAuth();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmationModal();
  const { hasBackendPermission } = usePermissions();
  const canEdit = hasBackendPermission("communication.sms_templates.edit");

  const currentCompany = React.useMemo(() => {
    const companyId = user?.company_id;
    if (companyId == null) return undefined;

    return userCompanies.find((c) => c.company?.id === companyId)?.company;
  }, [user?.company_id, userCompanies]);
  const companyName = currentCompany?.name?.trim() || "";
  const companyPhone = currentCompany?.phone?.trim() || "";
  const companyEmail = currentCompany?.mail_address?.trim() || "";

  const [placeholders, setPlaceholders] = React.useState<SMSPlaceholderDefinition[]>([]);
  const [templates, setTemplates] = React.useState<SMSTemplate[]>([]);
  const [template, setTemplate] = React.useState<SMSTemplate | undefined>(undefined);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const bodyRef = React.useRef<SMSMessageChipEditorHandle | null>(null);
  const selectedIdRef = React.useRef<number | undefined>(undefined);

  React.useEffect(() => {
    selectedIdRef.current = template?.id;
  }, [template?.id]);

  const reloadList = React.useCallback(
    async (preferId?: number) => {
      setLoading(true);
      try {
        const [ph, tpls] = await Promise.all([listSMSPlaceholders(), listSMSTemplates()]);
        if (ph) setPlaceholders(ph);
        const list = tpls ?? [];
        setTemplates(list);

        const wantId = preferId ?? selectedIdRef.current;
        if (wantId != null) {
          const found = list.find((t) => t.id === wantId);
          if (found) {
            setTemplate({ ...found });

            return;
          }
        }

        if (list.length > 0) {
          setTemplate({ ...list[0] });
        } else {
          setTemplate(undefined);
        }
      } finally {
        setLoading(false);
      }
    },
    [listSMSPlaceholders, listSMSTemplates],
  );

  React.useEffect(() => {
    void reloadList();
  }, [reloadList]);

  const onSelectTemplateId = React.useCallback(
    (id: number) => {
      const found = templates.find((t) => t.id === id);
      if (found) setTemplate({ ...found });
    },
    [templates],
  );

  const onNewTemplate = React.useCallback(() => {
    setTemplate(emptyDraft());
  }, []);

  const onSave = React.useCallback(async () => {
    if (!template || saving) return;
    if (!template.name.trim() || !template.body.trim()) {
      showAlert("Nom et message requis", "warning");

      return;
    }

    const ok = await showConfirmation(
      template.id ? "Modifier le modèle" : "Créer le modèle",
      template.id ? "Enregistrer ce modèle SMS ?" : "Créer ce modèle SMS ?",
    );
    if (!ok) return;

    setSaving(true);
    try {
      if (template.id) {
        const updated = await updateSMSTemplate(template.id, template);
        if (updated !== undefined) {
          showAlert("Modèle enregistré", "success");
          await reloadList(template.id);
        }
      } else {
        const created = await createSMSTemplate(template);
        if (created?.id) {
          showAlert("Modèle créé", "success");
          await reloadList(created.id);
        }
      }
    } finally {
      setSaving(false);
    }
  }, [
    createSMSTemplate,
    reloadList,
    saving,
    showAlert,
    showConfirmation,
    template,
    updateSMSTemplate,
  ]);

  const onDelete = React.useCallback(async () => {
    if (!template?.id) return;
    const ok = await showConfirmation("Supprimer le modèle", `Supprimer « ${template.name} » ?`);
    if (!ok) return;
    await deleteSMSTemplate(template.id);
    showAlert("Modèle supprimé", "success");
    selectedIdRef.current = undefined;
    setTemplate(undefined);
    await reloadList();
  }, [deleteSMSTemplate, reloadList, showAlert, showConfirmation, template]);

  const onActiveChange = React.useCallback(
    async (nextActive: boolean) => {
      if (!template?.id || !canEdit) return;
      const prev = template;
      const next = { ...template, active: nextActive };
      setTemplate(next);
      const updated = await updateSMSTemplate(template.id, next);
      if (updated !== undefined) {
        setTemplates((list) =>
          list.map((t) => (t.id === template.id ? { ...t, active: nextActive } : t)),
        );
        showAlert(nextActive ? "Modèle activé" : "Modèle désactivé", "success");
      } else {
        setTemplate(prev);
      }
    },
    [canEdit, showAlert, template, updateSMSTemplate],
  );

  const insertPlaceholder = React.useCallback(
    (key: string) => {
      if (!template || !canEdit) return;
      bodyRef.current?.insertPlaceholder(key);
    },
    [canEdit, template],
  );

  const previewCatalog = React.useMemo(() => {
    return placeholders.map((ph) => {
      if (ph.key === "CompanyName" && companyName) return { ...ph, example: companyName };
      if (ph.key === "ReplyPhone" && companyPhone) return { ...ph, example: companyPhone };
      if (ph.key === "CompanyEmail" && companyEmail) return { ...ph, example: companyEmail };

      return ph;
    });
  }, [companyEmail, companyName, companyPhone, placeholders]);

  const previewText = React.useMemo(() => {
    return renderSMSPreviewBody(template?.body ?? "", previewCatalog);
  }, [previewCatalog, template?.body]);

  return {
    canEdit,
    loading,
    saving,
    templates,
    template,
    setTemplate,
    placeholders,
    previewText,
    bodyRef,
    onSelectTemplateId,
    onNewTemplate,
    onSave,
    onDelete,
    onActiveChange,
    insertPlaceholder,
  };
}
